import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Box, Grid, Typography, Paper, Button, Chip, Skeleton, Alert, Stack, Divider
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import GitHubIcon from '@mui/icons-material/GitHub';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import API_BASE_URL from '../../Config/config';

const STATUS_META = {
  LOCKED: { label: 'Locked', bg: '#F1F5F9', fg: '#6B7280' },
  NOT_STARTED: { label: 'Not Started', bg: '#F1F5F9', fg: '#374151' },
  ACTIVE: { label: 'In Progress', bg: 'rgba(245,158,11,0.12)', fg: '#B45309' },
  IN_PROGRESS: { label: 'In Progress', bg: 'rgba(245,158,11,0.12)', fg: '#B45309' },
  UNDER_REVIEW: { label: 'Under Review', bg: 'rgba(2,132,199,0.12)', fg: '#0369A1' },
  COMPLETED: { label: 'Completed', bg: 'rgba(34,197,94,0.12)', fg: '#16A34A' }
};

// The student journey through one version, in order. ACTIVE and IN_PROGRESS
// are the same stage under two historical names.
const JOURNEY = [
  { key: 'NOT_STARTED', label: 'Kickoff', icon: FlagRoundedIcon },
  { key: 'IN_PROGRESS', label: 'Building', icon: RocketLaunchRoundedIcon },
  { key: 'UNDER_REVIEW', label: 'Review', icon: TrackChangesRoundedIcon },
  { key: 'COMPLETED', label: 'Completed', icon: CheckCircleRoundedIcon }
];
const journeyIndex = (status) => {
  const s = status === 'ACTIVE' ? 'IN_PROGRESS' : status;
  const i = JOURNEY.findIndex((j) => j.key === s);
  return i < 0 ? 0 : i;
};

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, delay, ease: 'easeOut' }
});

const HeroStat = ({ icon: Icon, label, value }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1.25,
    bgcolor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)',
    backdropFilter: 'blur(8px)', borderRadius: '14px', px: 2, py: 1.25, minWidth: 0
  }}>
    <Icon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.85)' }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: 'white', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const DetailItem = ({ icon: Icon, label, value }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
    {Icon && (
      <Box sx={{ p: 0.9, borderRadius: '10px', bgcolor: 'rgba(108,59,255,0.08)', display: 'flex', mt: 0.25 }}>
        <Icon sx={{ fontSize: 17, color: '#6C3BFF' }} />
      </Box>
    )}
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }}>{value || 'Not provided'}</Typography>
    </Box>
  </Box>
);

const NextGenProjectVersion = () => {
  const { versionId } = useParams();
  const navigate = useNavigate();
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/project-version/${versionId}`);
        if (res.data.success) setVersion(res.data.data);
      } catch (err) {
        if (err.response?.status === 403) {
          setLocked(true);
          setError(err.response?.data?.message || 'This project version is locked. Complete the previous version first.');
        } else {
          setError(err.response?.data?.message || 'Failed to load project version');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchVersion();
  }, [versionId]);

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Skeleton variant="rectangular" height={280} sx={{ borderRadius: '28px', mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}><Skeleton variant="rectangular" height={360} sx={{ borderRadius: '24px' }} /></Grid>
          <Grid item xs={12} md={4}><Skeleton variant="rectangular" height={360} sx={{ borderRadius: '24px' }} /></Grid>
        </Grid>
      </Box>
    );
  }

  if (locked) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper elevation={0} sx={{ p: 6, borderRadius: '24px', textAlign: 'center', maxWidth: 520, border: '1px solid #E5E7EB' }}>
          <LockRoundedIcon sx={{ fontSize: 64, color: '#6B7280', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827', mb: 1 }}>Version Locked</Typography>
          <Typography variant="body1" sx={{ color: '#374151', mb: 4 }}>{error}</Typography>
          <Button variant="contained" onClick={() => navigate('/nextgen-builder/dashboard')}
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #6C3BFF, #8B5CF6)' }}>
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!version) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Alert severity="error" sx={{ borderRadius: '12px' }}>{error || 'Project version not found'}</Alert>
      </Box>
    );
  }

  const meta = STATUS_META[version.status] || STATUS_META.NOT_STARTED;
  const repo = version.version_repository_url || version.project_repository_url;
  const techList = Array.from(new Set([...(version.tech_stacks || []), ...(version.languages || [])].map(String).filter(Boolean)));
  const versionLabel = version.project_version_name || `V${version.version_number}.0`;
  const currentStage = journeyIndex(version.status);
  const journeyPct = (currentStage / (JOURNEY.length - 1)) * 100;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1240, mx: 'auto' }}>
      <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/nextgen-builder/dashboard')}
        sx={{ mb: 2, textTransform: 'none', fontWeight: 700, color: '#6C3BFF' }}>
        Back to Dashboard
      </Button>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0)}>
        <Paper elevation={0} sx={{
          p: { xs: 3, md: 5 }, borderRadius: '28px', mb: 3, color: 'white',
          position: 'relative', overflow: 'hidden',
          background: 'linear-gradient(120deg, #6C3BFF 0%, #4C24C9 55%, #2E1578 100%)',
          boxShadow: '0 24px 60px rgba(76, 36, 201, 0.35)'
        }}>
          {/* decorative glows */}
          <Box sx={{ position: 'absolute', width: 420, height: 420, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.07)', top: -180, right: -120 }} />
          <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', bgcolor: 'rgba(0,209,255,0.12)', bottom: -140, left: '32%', filter: 'blur(6px)' }} />
          <RocketLaunchRoundedIcon sx={{ position: 'absolute', fontSize: 300, right: -50, bottom: -70, opacity: 0.08, transform: 'rotate(-12deg)', display: { xs: 'none', md: 'block' } }} />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap', rowGap: 1 }}>
              <Chip label={versionLabel}
                sx={{ bgcolor: 'white', color: '#4C24C9', fontWeight: 900, letterSpacing: '0.02em' }} />
              <Chip label={meta.label}
                icon={version.status === 'COMPLETED' ? <CheckCircleRoundedIcon sx={{ color: 'white !important', fontSize: 18 }} /> : undefined}
                sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: 'white', fontWeight: 700, border: '1px solid rgba(255,255,255,0.25)' }} />
              {version.preferred_role && (
                <Chip icon={<WorkOutlineRoundedIcon sx={{ color: 'white !important', fontSize: 16 }} />} label={version.preferred_role}
                  sx={{ bgcolor: 'rgba(255,255,255,0.10)', color: 'white', fontWeight: 700, display: { xs: 'none', sm: 'flex' } }} />
              )}
            </Stack>

            <Typography variant="h2" sx={{ fontWeight: 900, mb: 1, fontSize: { xs: '2.4rem', md: '3.4rem' }, letterSpacing: '-0.02em' }}>
              {version.project_name}
            </Typography>
            {version.vision && (
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500, fontStyle: 'italic', maxWidth: 760, mb: 3, lineHeight: 1.55 }}>
                “{version.vision}”
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              {version.preferred_role && <HeroStat icon={WorkOutlineRoundedIcon} label="Your Role" value={version.preferred_role} />}
              {version.sprint_duration && <HeroStat icon={AccessTimeRoundedIcon} label="Sprint" value={version.sprint_duration} />}
              {version.default_team_size && <HeroStat icon={GroupsRoundedIcon} label="Team" value={`${version.default_team_size} members`} />}
              <HeroStat icon={CalendarMonthRoundedIcon} label="Started"
                value={version.start_date ? new Date(version.start_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not started'} />
              {repo && (
                <Button variant="contained" startIcon={<GitHubIcon />} component="a" href={repo} target="_blank" rel="noopener noreferrer"
                  sx={{
                    ml: { sm: 'auto' }, borderRadius: '14px', textTransform: 'none', fontWeight: 800,
                    bgcolor: 'white', color: '#4C24C9', px: 3, py: 1.25,
                    '&:hover': { bgcolor: '#F1F5F9' }
                  }}>
                  Open Repository
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </motion.div>

      {/* ── Journey stepper ──────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.08)}>
        <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: '24px', border: '1px solid #E5E7EB', mb: 3 }}>
          <Box sx={{ position: 'relative', px: { xs: 1, md: 4 } }}>
            {/* track */}
            <Box sx={{ position: 'absolute', top: 22, left: { xs: 24, md: 56 }, right: { xs: 24, md: 56 }, height: 5, bgcolor: '#EEF2F7', borderRadius: 3 }} />
            <Box sx={{ position: 'absolute', top: 22, left: { xs: 24, md: 56 }, width: `calc((100% - ${'112px'}) * ${journeyPct / 100})`, height: 5, borderRadius: 3, background: 'linear-gradient(90deg, #6C3BFF, #8B5CF6)', transition: 'width 0.6s ease', display: { xs: 'none', md: 'block' } }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
              {JOURNEY.map((stage, i) => {
                const done = i < currentStage;
                const active = i === currentStage;
                const Icon = stage.icon;
                return (
                  <Box key={stage.key} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: done || active ? '#6C3BFF' : 'white',
                      border: '3px solid', borderColor: done || active ? '#6C3BFF' : '#E2E8F0',
                      boxShadow: active ? '0 0 0 6px rgba(108,59,255,0.15)' : 'none',
                      transition: 'all 0.3s'
                    }}>
                      <Icon sx={{ fontSize: 21, color: done || active ? 'white' : '#94A3B8' }} />
                    </Box>
                    <Typography variant="caption" sx={{
                      fontWeight: active ? 800 : 600,
                      color: active ? '#6C3BFF' : done ? '#334155' : '#94A3B8',
                      textAlign: 'center'
                    }}>
                      {stage.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Paper>
      </motion.div>

      <Grid container spacing={3}>
        {/* ── Main column ─────────────────────────────────────────────────── */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            <motion.div {...fadeUp(0.14)}>
              <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: '24px', border: '1px solid #E5E7EB' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box sx={{ p: 1, borderRadius: '12px', bgcolor: 'rgba(108,59,255,0.08)', display: 'flex' }}>
                    <RocketLaunchRoundedIcon sx={{ color: '#6C3BFF', fontSize: 20 }} />
                  </Box>
                  Project Overview
                </Typography>
                <Typography variant="body1" sx={{ color: '#4B5563', lineHeight: 1.85, whiteSpace: 'pre-wrap', fontSize: '0.98rem' }}>
                  {version.project_description || 'No description provided for this project.'}
                </Typography>
              </Paper>
            </motion.div>

            {techList.length > 0 && (
              <motion.div {...fadeUp(0.2)}>
                <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: '24px', border: '1px solid #E5E7EB' }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box sx={{ p: 1, borderRadius: '12px', bgcolor: 'rgba(0,209,255,0.1)', display: 'flex' }}>
                      <LayersRoundedIcon sx={{ color: '#0891B2', fontSize: 20 }} />
                    </Box>
                    Technology Stack
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {techList.map((t) => (
                      <Chip key={t} label={t} sx={{
                        fontWeight: 700, bgcolor: '#F8FAFC', border: '1px solid #E5E7EB', color: '#334155',
                        '&:hover': { bgcolor: 'rgba(108,59,255,0.06)', borderColor: '#C4B5FD' }
                      }} />
                    ))}
                  </Box>
                </Paper>
              </motion.div>
            )}

            {version.framework || version.database_type || version.architecture || version.primary_language ? (
              <motion.div {...fadeUp(0.26)}>
                <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: '24px', border: '1px solid #E5E7EB' }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box sx={{ p: 1, borderRadius: '12px', bgcolor: 'rgba(234,88,12,0.09)', display: 'flex' }}>
                      <AccountTreeRoundedIcon sx={{ color: '#EA580C', fontSize: 20 }} />
                    </Box>
                    Technical Specifications
                  </Typography>
                  <Grid container spacing={2.5}>
                    {version.primary_language && (
                      <Grid item xs={12} sm={6}><DetailItem icon={CodeRoundedIcon} label="Language" value={version.primary_language} /></Grid>
                    )}
                    {version.framework && (
                      <Grid item xs={12} sm={6}><DetailItem icon={LayersRoundedIcon} label="Framework" value={version.framework} /></Grid>
                    )}
                    {version.database_type && (
                      <Grid item xs={12} sm={6}><DetailItem icon={StorageRoundedIcon} label="Database" value={version.database_type} /></Grid>
                    )}
                    {version.architecture && (
                      <Grid item xs={12} sm={6}><DetailItem icon={AccountTreeRoundedIcon} label="Architecture" value={version.architecture} /></Grid>
                    )}
                  </Grid>
                </Paper>
              </motion.div>
            ) : null}
          </Stack>
        </Grid>

        {/* ── Sidebar ─────────────────────────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <motion.div {...fadeUp(0.18)}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', border: '1px solid #E5E7EB' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 2.5 }}>Version Details</Typography>
                <Stack spacing={2.25} divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
                  <DetailItem icon={AutoAwesomeRoundedIcon} label="Version" value={versionLabel} />
                  <DetailItem icon={WorkOutlineRoundedIcon} label="Your Role" value={version.preferred_role} />
                  <DetailItem icon={AccessTimeRoundedIcon} label="Sprint Duration" value={version.sprint_duration} />
                  <DetailItem icon={GroupsRoundedIcon} label="Team Size" value={version.default_team_size ? `${version.default_team_size} members` : null} />
                  <DetailItem icon={CalendarMonthRoundedIcon} label="Started"
                    value={version.start_date ? new Date(version.start_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not started'} />
                  {version.completed_date && (
                    <DetailItem icon={CheckCircleRoundedIcon} label="Completed"
                      value={new Date(version.completed_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })} />
                  )}
                </Stack>
              </Paper>
            </motion.div>

            {repo && (
              <motion.div {...fadeUp(0.24)}>
                <Paper elevation={0} sx={{
                  p: 3, borderRadius: '24px', color: 'white',
                  background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ p: 1, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.12)', display: 'flex' }}>
                      <GitHubIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Repository</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2.5, lineHeight: 1.6 }}>
                    All your team's code, branches and pull requests live here.
                  </Typography>
                  <Button fullWidth variant="contained" startIcon={<GitHubIcon />} component="a" href={repo} target="_blank" rel="noopener noreferrer"
                    sx={{ bgcolor: 'white', color: '#111827', fontWeight: 800, textTransform: 'none', borderRadius: '12px', '&:hover': { bgcolor: '#F1F5F9' } }}>
                    Open on GitHub
                  </Button>
                </Paper>
              </motion.div>
            )}

            <motion.div {...fadeUp(0.3)}>
              <Paper elevation={0} sx={{
                p: 3, borderRadius: '24px', border: '1px solid rgba(108,59,255,0.25)',
                bgcolor: 'rgba(108,59,255,0.04)'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#3D1E99', mb: 0.75 }}>
                  {version.status === 'COMPLETED' ? 'Version completed — well done! 🎉' : 'Keep shipping 🚀'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#5B21B6', lineHeight: 1.65 }}>
                  {version.status === 'COMPLETED'
                    ? 'Your next version unlocks automatically. Check your dashboard for what comes next.'
                    : 'Commit early, push often, and open pull requests for review — your AI code reviews and mentors are watching the repository.'}
                </Typography>
              </Paper>
            </motion.div>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NextGenProjectVersion;
